/** @jsxImportSource @emotion/react */
import { css, keyframes, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { ESocketEvents } from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import { useRpSocketContext } from 'providers/RpSocketProvider'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useKey } from 'react-use'
import { DepositIcon } from 'rpComponents/Icons'
import { Typography } from 'rpComponents/Typography'
import { Currency } from '../../components'
import { rpMediaQueries } from '../../core/theme'
import { useRussianPokerStore } from '../../providers/RussianPokerProvider'
import { useStore } from '../../stores'
import { numberWithSpaces } from '../../utils/number'
import { BtnGold } from '../Btn'
import { CloseBtn } from '../CloseBtn'
import img from './img/pattern_seamless.png'

// 1) Keyframes для overlay
const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`
const overlayFadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`

// 2) Keyframes для самого попапа
const popupIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`
const popupOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.98);
  }
`

// 3) Новые стили для контейнера
const AnimatedContainer = styled.div<{ animateOut: boolean }>`
  position: fixed;
  inset: 0;
  background: url(${img}) repeat;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99;

  animation: ${({ animateOut }) =>
      animateOut ? overlayFadeOut : overlayFadeIn}
    0.3s ease-in-out forwards;
`

// 4) Попап с анимацией
const AnimatedPopup = styled.div<{ animateOut: boolean }>`
  position: relative;
  max-width: 300px;
  padding: 10px 30px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.9);
  box-shadow: 0 0 60px 0 rgba(255, 196, 69, 0.3),
    0 10px 70px 0 rgba(255, 255, 255, 0.2) inset, 0 1px 0 0 rgba(0, 0, 0, 0.05),
    0 4px 4px 0 rgba(0, 0, 0, 0.05), 0 10px 10px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(1px);

  animation: ${({ animateOut }) => (animateOut ? popupOut : popupIn)} 0.3s
    ease-in-out forwards;

  ${rpMediaQueries.tablet} {
    padding: 20px 40px;
    max-width: 500px;
  }
`

// Остальные styled-компоненты без изменений
const Line = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;

  ${rpMediaQueries.tablet} {
    padding: 16px 0;
  }
`
const Tooltip = styled(Line)`
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  font-size: 9px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  ${rpMediaQueries.tablet} {
    font-size: 14px;
  }
`
const Text = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`
const InputContainers = styled.div`
  display: flex;
  gap: 5px;

  ${rpMediaQueries.tablet} {
    gap: 10px;
  }
`
const InputContainer = styled.div<{ disabled?: boolean }>`
  border-radius: 10px;
  padding: 2px 5px 2px 30px;
  position: relative;
  border: 1px solid transparent;
  ${({ disabled }) =>
    disabled
      ? css`
          background: transparent;
          border-color: rgba(255, 255, 255, 0.1);
          * {
            pointer-events: none;
          }
        `
      : css`
          background: linear-gradient(
            0deg,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(255, 255, 255, 0.05) 88.89%
          );
          border: 1px solid transparent;
        `}

  ${rpMediaQueries.tablet} {
    padding: 5px 10px 5px 35px;
  }
`
const Input = styled.input`
  outline: none;
  border: none;
  background: transparent;
  color: white;
  font-size: 18px;
  line-height: 1;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
`
const InputIconContainer = styled.div`
  position: absolute;
  width: 30px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  top: 0;

  ${rpMediaQueries.tablet} {
    width: 35px;
  }
`

const StyledCloseBtn = styled(CloseBtn)`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.05), 0 4px 4px 0 rgba(0, 0, 0, 0.05),
    0 10px 10px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.25);

  ${rpMediaQueries.tablet} {
    padding: 6px;
    top: 12px;
    right: 12px;
  }
`

const RpDepositRefillPopup: React.FC = observer(() => {
  const {
    uiStore,
    patchStore: patch,
    toastSuccess,
    toastError,
    gameId,
  } = useRussianPokerStore()
  const { emit } = useRpSocketContext()
  const {
    applicationStore: { gameRatio },
    applicationStore,
  } = useStore()
  const { t } = useTranslation()
  const theme = useTheme()

  const availableBalance = Number(applicationStore.availableBalance)
  const maxGameCoins = Math.floor(availableBalance / gameRatio)

  const [gameCoins, setGameCoins] = useState('')

  const physicalAmount =
    gameCoins === '' ? '' : (Number(gameCoins) * gameRatio).toString()

  const [visible, setVisible] = useState(uiStore.viewRefillPopup)
  const [animateOut, setAnimateOut] = useState(false)

  useKey('Escape', () => {
    uiStore.partialUpdate({ viewRefillPopup: false })
  })

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined

    if (uiStore.viewRefillPopup) {
      setVisible(true)
      setAnimateOut(false)
    } else if (visible) {
      setAnimateOut(true)
      timer = setTimeout(() => setVisible(false), 300)
    }

    return () => {
      if (timer) clearTimeout(timer)

      setGameCoins('')
    }
  }, [uiStore.viewRefillPopup, visible])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (gameCoins === '' || gameCoins === '0') {
      return
    }

    const deposit = Number(gameCoins)

    emit(
      ESocketEvents.DEPOSIT,
      {
        deposit,
        gameId,
      },
      (response: { success: boolean; deposit: number }) => {
        if (response.success) {
          patch({ deposit: response.deposit })
          toastSuccess(t('depositSuccessful'))
        } else {
          toastError(t('depositError'))
        }

        uiStore.partialUpdate({ viewRefillPopup: false })
      }
    )
  }

  if (!visible) return null

  return (
    <AnimatedContainer animateOut={animateOut}>
      <AnimatedPopup animateOut={animateOut}>
        <StyledCloseBtn
          circle
          size={{
            mobile: '6px',
            tablet: '12px',
            desktop: '12px',
          }}
          data-cy="close-refill-deposit-popup-btn"
          onClick={() => uiStore.partialUpdate({ viewRefillPopup: false })}
        />
        <form onSubmit={handleSubmit} data-cy="rp-refill-deposit-popup-form">
          <Typography
            variant="head1"
            color="white"
            as="h1"
            css={{ textAlign: 'center' }}>
            {t('balanceReplenishment')}
          </Typography>

          <Line>
            <Text css={{ display: 'flex', alignItems: 'center' }}>
              {numberWithSpaces(maxGameCoins)}&nbsp;
              <DepositIcon
                color="gold1"
                size={{
                  mobile: '16px',
                  tablet: '16px',
                  desktop: '16px',
                }}
              />
            </Text>
            <Text css={{ padding: '0 5px' }}>=</Text>
            <Text>
              {numberWithSpaces(availableBalance)}{' '}
              <Currency color={theme.rpTheme.colors.gold1} />
            </Text>
          </Line>

          <InputContainers>
            <InputContainer>
              <InputIconContainer>
                <DepositIcon size="18px" color="gold1" />
              </InputIconContainer>
              <Input
                data-cy="deposit-amount-input"
                value={gameCoins ? numberWithSpaces(Number(gameCoins)) : ''}
                placeholder="0"
                autoFocus
                type="text" // <-- было "tel"
                inputMode="numeric" // даёт цифровую клаву
                enterKeyHint="done" // подсказываем, что клавиша = "Готово"
                onChange={(e) => {
                  const val = e.target.value.replace(/\s/g, '')
                  if (val === '') {
                    setGameCoins('')
                    return
                  }
                  if (!/^\d+$/.test(val)) return
                  let num = Number(val)
                  if (num > maxGameCoins) num = maxGameCoins
                  setGameCoins(num.toString())
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    const { form } = e.currentTarget
                    if (form) {
                      if (typeof form.requestSubmit === 'function') {
                        form.requestSubmit()
                      } else {
                        form.submit()
                      }
                    }
                  }
                }}
              />
            </InputContainer>

            <InputContainer disabled>
              <InputIconContainer>
                <Currency
                  color={theme.rpTheme.colors.gold1}
                  css={{ fontSize: '18px', lineHeight: '1' }}
                />
              </InputIconContainer>
              <Input
                type="text"
                value={
                  physicalAmount ? numberWithSpaces(Number(physicalAmount)) : ''
                }
                placeholder="0"
                disabled
              />
            </InputContainer>
          </InputContainers>

          <Tooltip>
            {t('exchangeWillOccurAtTheRate')} {numberWithSpaces(gameRatio)}
            &nbsp;
            <Currency />
          </Tooltip>

          <div css={{ display: 'flex', gap: '3px', flexDirection: 'column' }}>
            <BtnGold
              data-cy="confirm-deposit-btn"
              type="submit"
              fullWidth
              disabled={gameCoins === '' || gameCoins === '0'}>
              <Typography variant="buttonBig" color="white" as="span">
                {t('continue')}
              </Typography>
            </BtnGold>
          </div>
        </form>
      </AnimatedPopup>
    </AnimatedContainer>
  )
})

export default RpDepositRefillPopup
